import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Tabs,
  Tab,
  Alert,
  Popover,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  Send,
  Delete,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  FormatColorText,
  SmartButton,
  DataObject,
  TextFields,
  AlignHorizontalLeft,
  AlignHorizontalCenter,
  Info,
  Unsubscribe as UnsubscribeIcon,
  Warning,
  FontDownload,
  Close,
} from '@mui/icons-material';
import RedoIcon from '@mui/icons-material/Redo';
import UndoIcon from '@mui/icons-material/Undo';
import { showSuccess, showError } from '../utils/toast';
import { colors } from '../theme/modernTheme';
import { emailTemplatesAPI, campaignsAPI } from '../services/api';
import { useLoading } from '../context/LoadingContext';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';

/**
 * EmailTemplateEditor Component
 * Full-page editor for creating and editing email templates
 * Validates: Requirements 1.1, 1.2, 7.2
 */
const EmailTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showLoading, hideLoading } = useLoading();
  const isEditMode = Boolean(id);

  // Get campaign context if coming from campaign page
  const campaignContext = location.state?.campaignData;
  const returnTo = location.state?.returnTo;
  const fromCampaign = location.state?.fromCampaign; // true if coming from campaign
  const selectedTags = location.state?.selectedTags || [];

  // Template state
  const [template, setTemplate] = useState({
    name: '',
    subject: '',
    html_body: "<p style='padding: 0 12px;'>Start typing your email content here...</p>",

    tags: [],
    is_default: false,
    notes: '',
    can_delete: true,
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Design, 1: HTML - Start with Design
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [testSendDialogOpen, setTestSendDialogOpen] = useState(false);
  const [testSendEmail, setTestSendEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [textColorAnchor, setTextColorAnchor] = useState(null);
  const [fontFamilyMenuAnchor, setFontFamilyMenuAnchor] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [buttonDialogOpen, setButtonDialogOpen] = useState(false);
  const [buttonUrl, setButtonUrl] = useState('');
  const [buttonText, setButtonText] = useState('Click Here');
  const [buttonColor, setButtonColor] = useState('#28a745');
  const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState(null);
  const [placeholderMenuAnchor, setPlaceholderMenuAnchor] = useState(null);
  const [templateAlignment, setTemplateAlignment] = useState('left'); // New state for template alignment
  const [unsubscribeModalOpen, setUnsubscribeModalOpen] = useState(false); // Unsubscribe info modal

  const previewIframeRef = useRef(null);
  const previousTabRef = useRef(0); // Track previous tab

  // Hard-coded dummy data for preview - Indian Jain person data
  const dummyData = {
    contact: {
      first_name: 'Adinath',
      last_name: 'Kothari',
      email: 'adinath.kothari@example.com',
      phone: '+91 98765 43210',
      address: 'Walkeshwar Road, Malabar Hill, Mumbai 400006',
      city: 'Mumbai',
      state: 'Maharashtra',
      country: 'India',
    },
    // donation: {
    //   amount: "₹10,000",
    //   date: "02 December 2024",
    //   currency: "INR",
    //   payment_method: "UPI",
    //   frequency: "One-time",
    //   purpose: "General Donation",
    //   receipt_number: "RCP-2024-0123",
    //   transaction_id: "TXN20241202001",
    // },
    // campaign: {
    //   name: "Annual Fundraiser 2024",
    //   description: "Supporting community welfare programs",
    //   amount_raised: "₹3,50,000",
    // },
    // org: {
    //   name: "Jain Sangh Mumbai",
    //   email: "info@jainsanghmumbai.org",
    //   phone: "+91 22 2363 3627",
    //   address: "Walkeshwar Road, Malabar Hill, Mumbai, Maharashtra 400006",
    // },
  };

  // Available placeholders
  const availablePlaceholders = [
    // Contact placeholders
    {
      token: '{{contact.first_name}}',
      label: 'Contact: First Name',
      group: 'Contact',
    },
    {
      token: '{{contact.last_name}}',
      label: 'Contact: Last Name',
      group: 'Contact',
    },
    { token: '{{contact.email}}', label: 'Contact: Email', group: 'Contact' },
    { token: '{{contact.phone}}', label: 'Contact: Phone', group: 'Contact' },
    {
      token: '{{contact.address}}',
      label: 'Contact: Address',
      group: 'Contact',
    },
    { token: '{{contact.city}}', label: 'Contact: City', group: 'Contact' },
    { token: '{{contact.state}}', label: 'Contact: State', group: 'Contact' },
    {
      token: '{{contact.country}}',
      label: 'Contact: Country',
      group: 'Contact',
    },
    { token: '{{transaction.amount}}', label: 'Amount', field: 'amount', group: 'transaction' },
    {
      token: '{{transaction.trust_name}}',
      label: 'Trust Name',
      field: 'trust_name',
      group: 'transaction',
    },
    {
      token: '{{transaction.donation_date}}',
      label: 'Donation Date',
      field: 'donation_date',
      group: 'transaction',
    },
    {
      token: '{{transaction.payment_method}}',
      label: 'Payment Method',
      field: 'payment_method',
      group: 'transaction',
    },
    {
      token: '[unsubscribe]',
      label: 'Unsubscribe Email',
      group: 'Campaign',
    },
  ];

  // Load template if in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Update preview when template content changes and preview tab is active
  useEffect(() => {
    if (activeTab === 1 && !id) {
      // Only update preview for unsaved templates when on preview tab
      let html = template.html_body || '<p>No content yet</p>';

      // Replace placeholder spans with dummy data
      // The editor stores placeholders as: <span data-placeholder="true" data-token="{{contact.first_name}}" ...>Label</span>
      Object.keys(dummyData).forEach((scope) => {
        Object.keys(dummyData[scope]).forEach((field) => {
          const token = `{{${scope}.${field}}}`;
          const value = dummyData[scope][field];

          // Escape special regex characters in the token
          const escapedToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

          // Replace placeholder spans that have this token
          const spanRegex = new RegExp(
            `<span[^>]*data-token="${escapedToken}"[^>]*>.*?</span>`,
            'g',
          );
          html = html.replace(spanRegex, value);

          // Also replace plain tokens (in case they exist)
          html = html.replace(new RegExp(escapedToken, 'g'), value);
        });
      });

      setPreviewHtml(html);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.html_body, activeTab]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+S or Cmd+S to save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, fieldErrors]);

  // Update iframe body alignment when templateAlignment changes
  useEffect(() => {
    if (previewIframeRef.current && activeTab === 0) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;
        if (iframeDoc.body) {
          iframeDoc.body.style.justifyContent =
            templateAlignment === 'center' ? 'center' : 'flex-start';

          // Also update table align attributes for full HTML documents
          const firstTable = iframeDoc.querySelector('body > table');
          if (firstTable) {
            const alignTd = firstTable.querySelector('td[align]');
            if (alignTd) {
              alignTd.setAttribute('align', templateAlignment === 'center' ? 'center' : 'left');
            }
          }
        }
      } catch (error) {
        console.error('Error updating template alignment:', error);
      }
    }
  }, [templateAlignment, activeTab]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const response = await emailTemplatesAPI.getById(id);
      const templateData = response.data || response;
      setTemplate(templateData);

      // Try to detect alignment from existing HTML structure
      if (templateData.html_body) {
        // Check if the HTML has center alignment in the table structure
        const hasCenterAlign = /align=["']center["']/i.test(templateData.html_body);
        setTemplateAlignment(hasCenterAlign ? 'center' : 'left');
      }
    } catch (error) {
      showError('Failed to load template');
      navigate('/email-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/email-templates');
  };

  const handleFieldChange = (field, value) => {
    // If changing html_body, preserve head content when it's a full HTML document
    if (field === 'html_body' && value) {
      const trimmedValue = value.trim().toLowerCase();
      if (trimmedValue.startsWith('<!doctype') || trimmedValue.startsWith('<html')) {
        // Don't extract - keep the full HTML document as-is
        // The generateAlignedHtml function will handle extraction when saving
      }
    }

    setTemplate((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!template.name || !template.name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!template.subject || !template.subject.trim()) {
      newErrors.subject = 'Subject line is required';
    }

    if (!template.html_body || !template.html_body.trim()) {
      newErrors.html_body = 'Template content is required';
    }

    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Generate email-ready HTML with alignment wrapper baked in
   * This creates proper table-based layout for email clients
   * Preserves head section for fonts and stylesheets
   * Applies body background color if provided
   */
  const generateAlignedHtml = (content, alignment, headContent = '', bodyColor = '') => {
    if (!content) return '';

    // Clean up the content
    let cleanContent = content.replace(/<title[^>]*>.*?<\/title>/gi, '');
    let extractedHead = headContent;
    let extractedBodyColor = bodyColor;

    // Check if content is already a full HTML document
    const trimmedContent = cleanContent.trim().toLowerCase();
    if (trimmedContent.startsWith('<!doctype') || trimmedContent.startsWith('<html')) {
      // Parse the document to extract head and body
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'text/html');

      // Extract head content if not already provided
      if (!extractedHead && doc.head) {
        let headHtml = doc.head.innerHTML;
        headHtml = headHtml.replace(/<meta[^>]*>/gi, '');
        headHtml = headHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
        headHtml = headHtml.replace(/<title[^>]*>.*?<\/title>/gi, '');
        extractedHead = headHtml.trim();
      }

      // Extract body background color if not already provided
      if (!extractedBodyColor && doc.body) {
        const bodyStyle = doc.body.getAttribute('style');
        if (bodyStyle) {
          const bgMatch = bodyStyle.match(/background(?:-color)?\s*:\s*([^;]+)/i);
          if (bgMatch) {
            extractedBodyColor = bgMatch[1].trim();
          }
        }
      }

      // Extract body content
      cleanContent = doc.body ? doc.body.innerHTML : cleanContent;
      cleanContent = cleanContent.replace(/<title[^>]*>.*?<\/title>/gi, '');

      // Check if the body content already has a table structure with alignment
      // If so, update the alignment in the existing structure instead of wrapping
      const hasTableStructure =
        /<table[^>]*width=["']100%["'][^>]*>[\s\S]*?<td[^>]*align=["'](center|left)["']/i.test(
          cleanContent,
        );

      if (hasTableStructure) {
        // Update alignment in existing table structure
        const alignAttr = alignment === 'center' ? 'center' : 'left';

        // Replace align attributes in the outer table's td
        cleanContent = cleanContent.replace(
          /(<td[^>]*?)align=["'](center|left)["']/gi,
          `$1align="${alignAttr}"`,
        );

        // Replace align attributes in inner tables
        cleanContent = cleanContent.replace(
          /(<table[^>]*?)align=["'](center|left)["']/gi,
          `$1align="${alignAttr}"`,
        );

        // Build head section
        const headSection = extractedHead
          ? `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${extractedHead}
</head>`
          : `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>`;

        // Apply body background color if provided
        const bodyStyle = extractedBodyColor
          ? `margin: 0; padding: 0; background-color: ${extractedBodyColor};`
          : 'margin: 0; padding: 0;';

        return `<!DOCTYPE html>
<html>
${headSection}
<body style="${bodyStyle}">
${cleanContent}
</body>
</html>`;
      }
    }

    // Generate table-based layout for email compatibility (for content without table structure)
    const alignAttr = alignment === 'center' ? 'center' : 'left';
    const marginStyle = alignment === 'center' ? 'margin: 0 auto;' : '';

    // Apply body background color if provided
    const bodyStyle = extractedBodyColor
      ? `margin: 0; padding: 0; background-color: ${extractedBodyColor};`
      : 'margin: 0; padding: 0;';

    // Build head section
    const headSection = extractedHead
      ? `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  ${extractedHead}
</head>`
      : `<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>`;

    return `<!DOCTYPE html>
<html>
${headSection}
<body style="${bodyStyle}">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin: 0; padding: 0;">
  <tr>
    <td align="${alignAttr}" valign="top" style="padding: 0;">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="${alignAttr}" style="width: 600px; max-width: 600px; ${marginStyle}">
        <tr>
          <td style="font-family: Arial, sans-serif;">
            ${cleanContent}
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>
</body>
</html>`;
  };

  const handleSave = async (saveOption = null) => {
    // If on Design tab (index 0), capture the latest content before saving
    if (activeTab === 0 && previewIframeRef.current) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;

        // Check if designMode was ever enabled (user clicked to edit)
        // If not, keep the original HTML to preserve all CSS properties
        if (iframeDoc.designMode === 'off') {
          // User never clicked to edit - preserve the HTML exactly as-is
          // Do NOT call generateAlignedHtml here because DOMParser strips
          // bgcolor attributes, DOCTYPE, HTML comments, and normalizes CSS
          console.log('Design mode was never enabled, preserving original HTML as-is');
        } else {
          // User edited in design mode, extract the modified content
          // Check if the original template was a full HTML document
          const trimmedHtml = template.html_body.trim().toLowerCase();
          const isFullDocument =
            trimmedHtml.startsWith('<!doctype') || trimmedHtml.startsWith('<html');

          if (isFullDocument) {
            // For full HTML documents, capture the entire document from the iframe
            // This preserves bgcolor, inline styles, and all original attributes
            // Using outerHTML avoids the stripping that body.innerHTML does
            template.html_body = '<!DOCTYPE html>\n' + iframeDoc.documentElement.outerHTML;
          } else {
            // For partial HTML (fragments), extract from the email-container wrapper
            const emailContainer = iframeDoc.querySelector('.email-container');
            let bodyContent = emailContainer
              ? emailContainer.innerHTML
              : iframeDoc.body
                ? iframeDoc.body.innerHTML
                : '';

            // Remove any <title> tags that might have been added
            bodyContent = bodyContent.replace(/<title[^>]*>.*?<\/title>/gi, '');

            // Extract head content from iframe if present
            let headContent = '';
            if (iframeDoc.head) {
              let headHtml = iframeDoc.head.innerHTML;
              // Remove meta tags and editor-added style tags, but preserve user's styles
              headHtml = headHtml.replace(/<meta[^>]*>/gi, '');
              headHtml = headHtml.replace(
                /<style[^>]*data-editor-style[^>]*>[\s\S]*?<\/style>/gi,
                '',
              );
              headHtml = headHtml.replace(/<title[^>]*>.*?<\/title>/gi, '');
              headContent = headHtml.trim();
            }

            // Extract body background color from iframe
            let bodyColor = '';
            if (iframeDoc.body) {
              const bodyStyle = iframeDoc.body.getAttribute('style');
              if (bodyStyle) {
                const bgMatch = bodyStyle.match(/background(?:-color)?\s*:\s*([^;]+)/i);
                if (bgMatch) {
                  bodyColor = bgMatch[1].trim();
                }
              }
              if (!bodyColor) {
                const computedStyle = iframeDoc.defaultView.getComputedStyle(iframeDoc.body);
                const bgColor = computedStyle.backgroundColor;
                if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
                  bodyColor = bgColor;
                }
              }
            }

            // Generate aligned HTML with table wrapper
            template.html_body = generateAlignedHtml(
              bodyContent,
              templateAlignment,
              headContent,
              bodyColor,
            );
          }
        }
      } catch (error) {
        console.error('Error capturing design content before save:', error);
      }
    }

    // If on HTML tab (index 1), only wrap if it's NOT already a full HTML document
    if (activeTab === 1) {
      const trimmedHtml = template.html_body.trim();
      const lowerHtml = trimmedHtml.toLowerCase();
      // Check for various ways a full HTML document might start
      const isFullDocument =
        lowerHtml.startsWith('<!doctype') ||
        lowerHtml.startsWith('<html') ||
        lowerHtml.startsWith('<!doctype html') ||
        trimmedHtml.startsWith('<!DOCTYPE');

      // If it's already a full HTML document, preserve it exactly as-is
      // Do NOT call generateAlignedHtml - DOMParser strips bgcolor, DOCTYPE, comments, etc.
      if (isFullDocument) {
        console.log('Full HTML document detected, preserving as-is');
      } else {
        // Only wrap partial HTML content
        template.html_body = generateAlignedHtml(template.html_body, templateAlignment);
      }
    }

    // Validate form
    if (!validateForm()) {
      // Don't show toast - field errors are displayed below each field
      return;
    }

    try {
      showLoading();
      let templateId;

      // Create template data - alignment is already baked into html_body
      const templateData = {
        ...template,
      };

      if (isEditMode) {
        // Update existing template
        await emailTemplatesAPI.update(id, templateData);
        templateId = id;
        showSuccess('Template updated successfully');
        navigate(`/email-templates/${templateId}`);
      } else {
        // Create new template
        const response = await emailTemplatesAPI.create(templateData);
        templateId = response.data?.id || response.id;
        showSuccess('Template created successfully');
        navigate(`/email-templates/${templateId}`);
      }

      // Handle campaign save if coming from campaign
      if (fromCampaign && campaignContext && templateId && saveOption) {
        try {
          let campaignStatus = 'DRAFT';
          let successMessage = 'Template saved successfully';

          // Determine campaign status based on save option
          if (saveOption === 'activate') {
            campaignStatus = 'ACTIVE';
            successMessage = 'Campaign activated successfully!';
          } else if (saveOption === 'draft') {
            campaignStatus = 'DRAFT';
            successMessage = 'Campaign saved as draft!';
          }

          const campaignData = {
            ...campaignContext,
            status: campaignStatus,
            email_template_id: templateId,
          };

          // Check if it's a new campaign or existing one
          const campaignIdMatch = returnTo?.match(/\/campaigns\/(.+)/);
          const campaignId = campaignIdMatch?.[1];

          let finalCampaignId;

          if (campaignId && campaignId !== 'new') {
            // Update existing campaign
            await campaignsAPI.update(campaignId, campaignData);
            finalCampaignId = campaignId;

            // For updates, still need to assign tags separately
            if (selectedTags.length > 0) {
              for (const tag of selectedTags) {
                try {
                  await campaignsAPI.assignTag(finalCampaignId, {
                    tag_id: tag.id,
                  });
                } catch (error) {}
              }
            }
          } else {
            // Create new campaign
            const campaignResponse = await campaignsAPI.create(campaignData);
            finalCampaignId = campaignResponse.data.id;

            // Assign tags after campaign creation
            if (selectedTags.length > 0) {
              for (const tag of selectedTags) {
                try {
                  await campaignsAPI.assignTag(finalCampaignId, {
                    tag_id: tag.id,
                  });
                } catch (error) {}
              }
            }
          }

          showSuccess(successMessage);
          // Navigate to the campaign detail page
          navigate(`/campaigns/${finalCampaignId}`);
          return;
        } catch (campaignError) {
          showError('Template saved but failed to save campaign');
        }
      }

      // Navigate back or to edit mode
      if (returnTo && !fromCampaign) {
        navigate(returnTo, {
          state: {
            selectedTemplateId: templateId,
            campaignData: campaignContext,
          },
        });
      } else if (templateId && !isEditMode) {
        navigate(`/email-templates/${templateId}`, { replace: true });
      }
    } catch (error) {
      // Debug log

      // The API interceptor already unwraps error.response.data, so error is the data object
      if (error.error?.code === 'VALIDATION_ERROR') {
        const errors = error.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        const errorMessage = error.error?.message || error.message || 'Failed to save template';

        // Handle specific errors
        if (errorMessage.includes('unique') || errorMessage.includes('already exists')) {
          setFieldErrors({ name: 'A template with this name already exists' });
          // Don't show toast - field error is displayed below name field
        } else {
          showError(errorMessage);
        }
      }
    } finally {
      hideLoading();
    }
  };

  const handleTestSendOpen = async () => {
    // Validate form first
    if (!validateForm()) {
      // Don't show toast - field errors are displayed below each field
      return;
    }

    // If on Design tab (index 0), capture the latest content before saving
    if (activeTab === 0 && previewIframeRef.current) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;

        // Get the content from inside the email-container div (not the wrapper itself)
        const emailContainer = iframeDoc.querySelector('.email-container');
        let bodyContent = emailContainer
          ? emailContainer.innerHTML
          : iframeDoc.body
            ? iframeDoc.body.innerHTML
            : '';

        // Remove any <title> tags that might have been added
        bodyContent = bodyContent.replace(/<title[^>]*>.*?<\/title>/gi, '');

        // Extract head content from iframe if present
        let headContent = '';
        if (iframeDoc.head) {
          let headHtml = iframeDoc.head.innerHTML;
          // Remove meta tags and style tags that we add programmatically
          headHtml = headHtml.replace(/<meta[^>]*>/gi, '');
          headHtml = headHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
          headHtml = headHtml.replace(/<title[^>]*>.*?<\/title>/gi, '');
          headContent = headHtml.trim();
        }

        // Extract body background color from iframe
        let bodyColor = '';
        if (iframeDoc.body) {
          // First try to get from style attribute (more accurate)
          const bodyStyle = iframeDoc.body.getAttribute('style');
          if (bodyStyle) {
            const bgMatch = bodyStyle.match(/background(?:-color)?\s*:\s*([^;]+)/i);
            if (bgMatch) {
              bodyColor = bgMatch[1].trim();
            }
          }

          // If not found in style attribute, fall back to computed style
          if (!bodyColor) {
            const computedStyle = iframeDoc.defaultView.getComputedStyle(iframeDoc.body);
            const bgColor = computedStyle.backgroundColor;
            // Only include if it's not transparent or default
            if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)' && bgColor !== 'transparent') {
              bodyColor = bgColor;
            }
          }
        }

        // Generate aligned HTML with table wrapper, passing head content and body color
        template.html_body = generateAlignedHtml(
          bodyContent,
          templateAlignment,
          headContent,
          bodyColor,
        );
      } catch (error) {
        console.error('Error capturing design content before save:', error);
      }
    }

    // If on HTML tab, only wrap if it's NOT already a full HTML document
    if (activeTab === 1) {
      const trimmedHtml = template.html_body.trim().toLowerCase();
      const isFullDocument = trimmedHtml.startsWith('<!doctype') || trimmedHtml.startsWith('<html');

      // If it's already a full HTML document, keep it as-is to preserve all styling
      if (!isFullDocument) {
        // Only wrap partial HTML content
        template.html_body = generateAlignedHtml(template.html_body, templateAlignment);
      }
      // If it's a full document, don't process it - save as-is
    }

    try {
      showLoading();
      let templateId = id;

      // Create template data - alignment is already baked into html_body
      const templateData = {
        ...template,
      };

      // Save the template first
      if (isEditMode) {
        // Update existing template
        await emailTemplatesAPI.update(id, templateData);
        showSuccess('Template saved successfully');
      } else {
        // Create new template
        const response = await emailTemplatesAPI.create(templateData);
        templateId = response.data?.id || response.id;
        showSuccess('Template created successfully');

        // Navigate to edit mode with the new template ID
        navigate(`/email-templates/${templateId}`, { replace: true });
      }

      // Open test send dialog after successful save
      setTestSendDialogOpen(true);
    } catch (error) {
      if (error.error?.code === 'VALIDATION_ERROR') {
        const errors = error.error.details?.field_errors || {};
        setFieldErrors(errors);
        // Don't show toast - field errors are displayed below each field
      } else {
        const errorMessage = error.error?.message || error.message || 'Failed to save template';
        showError(errorMessage);
      }
    } finally {
      hideLoading();
    }
  };

  const handleTestSendClose = () => {
    setTestSendDialogOpen(false);
    setTestSendEmail('');
  };

  const handleTestSend = async () => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!testSendEmail || !emailRegex.test(testSendEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    setTestSending(true);
    try {
      // Send test email - backend will use dummy data automatically
      await emailTemplatesAPI.testSend(id, testSendEmail);
      showSuccess(`Test email sent to ${testSendEmail} with dummy data`);
      handleTestSendClose();
    } catch (error) {
      const errorMessage = error.message || error.error?.message || 'Failed to send test email';
      showError(errorMessage);
    } finally {
      setTestSending(false);
    }
  };

  // Handle tab change and load preview (Bug #9)
  const handleTabChange = async (event, newValue) => {
    const previousTab = previousTabRef.current;

    // When switching FROM Design tab (now index 0), save the content
    if (previousTab === 0 && previewIframeRef.current) {
      try {
        const iframeDoc =
          previewIframeRef.current.contentDocument ||
          previewIframeRef.current.contentWindow.document;
        iframeDoc.designMode = 'off';

        // Get the content from inside the email-container div (not the wrapper itself)
        const emailContainer = iframeDoc.querySelector('.email-container');
        let bodyContent = emailContainer
          ? emailContainer.innerHTML
          : iframeDoc.body
            ? iframeDoc.body.innerHTML
            : '';

        // Remove any <title> tags that might have been added
        bodyContent = bodyContent.replace(/<title[^>]*>.*?<\/title>/gi, '');

        // Extract head content from iframe if present
        let headContent = '';
        if (iframeDoc.head) {
          let headHtml = iframeDoc.head.innerHTML;
          // Remove meta tags and style tags that we add programmatically
          headHtml = headHtml.replace(/<meta[^>]*>/gi, '');
          headHtml = headHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
          headHtml = headHtml.replace(/<title[^>]*>.*?<\/title>/gi, '');
          headContent = headHtml.trim();
        }

        // Extract body background color from iframe
        let bodyColor = '';
        if (iframeDoc.body) {
          const bodyStyle = iframeDoc.body.getAttribute('style');
          if (bodyStyle) {
            const bgMatch = bodyStyle.match(/background(?:-color)?\s*:\s*([^;]+)/i);
            if (bgMatch) {
              bodyColor = bgMatch[1].trim();
            }
          }
        }

        // Generate aligned HTML with table wrapper, preserving head and body color
        const fullHtml = generateAlignedHtml(
          bodyContent,
          templateAlignment,
          headContent,
          bodyColor,
        );

        handleFieldChange('html_body', fullHtml);
      } catch (error) {
        console.error('Error saving design content:', error);
      }
    }

    setActiveTab(newValue);
    previousTabRef.current = newValue;

    // No preview loading needed anymore since we only have Design and HTML tabs
  };

  const handleDeleteOpen = () => {
    if (template.can_delete === false) {
      showError('Cannot delete this template as it is currently being used in active campaigns.');
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      showLoading();
      // Soft delete by default
      await emailTemplatesAPI.delete(id);
      showSuccess('Template deleted successfully');
      navigate('/email-templates');
    } catch (error) {
      const errorMessage = error.message || error.error?.message || 'Failed to delete template';
      showError(errorMessage);
    } finally {
      hideLoading();
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: colors.primary.main, mb: 3 }} />
        <Typography variant="h6" sx={{ color: colors.text.secondary, fontWeight: 500 }}>
          Loading template...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.background.default,
      }}
    >
      {/* ARIA Live Region for Error Announcements */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {Object.values(fieldErrors).filter(Boolean).join('. ')}
      </div>

      {/* Campaign Context Banner */}
      {campaignContext && fromCampaign && (
        <Box
          sx={{
            backgroundColor: colors.action.hover,
            borderBottom: `1px solid ${colors.action.selected}`,
            px: 3,
            py: 1.5,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: colors.primary.dark,
              fontWeight: 500,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            Creating email template for campaign:{' '}
            <strong>{campaignContext.name || 'New Campaign'}</strong>
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: colors.primary.dark, mt: 0.5, display: 'block' }}
          >
            Template will be saved as draft and you'll be redirected to the campaign
          </Typography>
        </Box>
      )}

      {/* Top Bar */}
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{
              mr: 2,
              color: colors.primary.main,
              '&:hover': {
                color: colors.primary.dark,
                backgroundColor: 'transparent !important',
              },
            }}
            aria-label="back to templates"
          >
            <ArrowBack />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              textTransform: 'capitalize',
              flexGrow: 1,
              fontWeight: 700,
              color: '#000000',
              fontFamily: '"Poppins", sans-serif',
            }}
            component="h1"
          >
            {isEditMode ? 'Edit Email Template' : 'Create Email Template'}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<Send />}
              onClick={handleTestSendOpen}
              aria-label="test send"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#E5E7EB',
                color: colors.text.secondary,
                borderRadius: '4px',
                '&:hover': {
                  borderColor: colors.primary.main,
                  backgroundColor: 'rgba(90, 106, 207, 0.04)',
                  color: colors.primary.main,
                },
                '&.Mui-disabled': {
                  borderColor: '#E5E7EB',
                  color: '#A6ABC8',
                },
              }}
            >
              Test Send
            </Button>

            {isEditMode && (
              <Tooltip
                title={
                  template.can_delete === false
                    ? 'Cannot delete: template is used in active campaigns'
                    : 'Delete template'
                }
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleDeleteOpen}
                    disabled={template.can_delete === false}
                    aria-label="delete template"
                    sx={{
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: template.can_delete === false ? '#E5E7EB' : '#FEE2E2',
                      color: template.can_delete === false ? '#A6ABC8' : '#DC2626',
                      '&:hover': {
                        borderColor: template.can_delete === false ? '#E5E7EB' : '#DC2626',
                        backgroundColor:
                          template.can_delete === false ? 'transparent' : 'rgba(220, 38, 38, 0.04)',
                      },
                      '&.Mui-disabled': {
                        borderColor: '#E5E7EB',
                        color: '#A6ABC8',
                        cursor: 'not-allowed',
                      },
                    }}
                  >
                    Delete
                  </Button>
                </span>
              </Tooltip>
            )}

            {fromCampaign && campaignContext ? (
              // Show only Save as Draft button when coming from campaign
              <Tooltip title="Save template and campaign as draft">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => handleSave('draft')}
                    aria-label="save as draft"
                    sx={{
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      backgroundColor: colors.primary.main,
                      '&:hover': {
                        backgroundColor: colors.primary.dark,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#A6ABC8',
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    Save as Draft
                  </Button>
                </span>
              </Tooltip>
            ) : (
              // Show single save button for normal template editing
              <Tooltip title="Save (Ctrl+S)">
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={() => handleSave()}
                    aria-label="save template"
                    sx={{
                      borderRadius: '4px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      backgroundColor: colors.primary.main,
                      '&:hover': {
                        backgroundColor: colors.primary.dark,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#A6ABC8',
                        color: '#FFFFFF',
                      },
                    }}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex' }}>
        {/* Left Panel: Template Details (Bug #5 - max 360px) */}
        <Box
          sx={{
            width: 360,
            maxWidth: 360,
            height: '100%',
            overflow: 'auto',
            borderRight: '1px solid #E5E7EB',
            backgroundColor: '#FFFFFF',
            flexShrink: 0,
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 700,
                color: '#000000',
                fontFamily: '"Poppins", sans-serif',
              }}
            >
              Template Details
            </Typography>

            {/* Template Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Template Name */}
              <TextField
                fullWidth
                required
                label="Template Name"
                value={template.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name || 'Unique name'}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                placeholder="e.g., Welcome Email"
                size="small"
                inputProps={{
                  'aria-label': 'template name',
                  'aria-required': 'true',
                }}
              />
              {/* Subject */}
              <TextField
                fullWidth
                required
                label="Subject"
                value={template.subject}
                onChange={(e) => handleFieldChange('subject', e.target.value)}
                placeholder="e.g., Welcome!"
                error={Boolean(fieldErrors.subject)}
                helperText={fieldErrors.subject || 'Email subject line'}
                size="small"
                inputProps={{
                  'aria-label': 'email subject',
                  'aria-required': 'true',
                }}
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Internal Notes"
                value={template.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                helperText="Not included in emails"
                placeholder="Add notes..."
                size="small"
                FormHelperTextProps={{
                  sx: { marginLeft: 0 },
                }}
                inputProps={{
                  'aria-label': 'internal notes',
                }}
              />

              {/* Please Note - Clickable Link */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                  cursor: 'pointer',
                }}
                onClick={() => setUnsubscribeModalOpen(true)}
              >
                <Info
                  sx={{
                    fontSize: 18,
                    color: '#ed6c02',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#ed6c02',
                    fontSize: '15px',
                    fontWeight: 500,
                  }}
                >
                  Please note for email template
                </Typography>
              </Box>

              {/* Audit Information (Edit Mode Only) */}
              {isEditMode && template.created_at && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: colors.text.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      Audit Info
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(template.created_at).toLocaleDateString()}
                      </Typography>
                      {template.updated_at && (
                        <Typography variant="caption" color="text.secondary">
                          Updated: {new Date(template.updated_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right Panel: Editor with Tabs (Bug #4) */}
        <Box
          sx={{
            flexGrow: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                minHeight: '36px',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 100,
                  minHeight: '36px',
                  fontSize: '0.813rem',
                  py: 0.5,
                },
              }}
            >
              <Tab label="Design" />
              <Tab label="HTML" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
            {/* Design Tab - Now first (index 0) */}
            {activeTab === 0 && (
              <Box sx={{ width: '100%', height: '100%', borderRadius: '4px' }}>
                {previewLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: 400,
                      gap: 2,
                    }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                      Loading preview...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* Formatting Toolbar - Always visible in Design mode */}
                    <Box sx={{ mb: 2 }}>
                      <Paper
                        elevation={1}
                        sx={{
                          borderRadius: '4px !important',
                          p: 0.75,
                          display: 'flex',
                          gap: 0.75,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          backgroundColor: '#F9FAFB',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <Tooltip title="Bold (Ctrl+B)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('bold', false, null);
                                } catch (error) {
                                  console.error('Error executing bold:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatBold fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Italic (Ctrl+I)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('italic', false, null);
                                } catch (error) {
                                  console.error('Error executing italic:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatItalic fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Underline (Ctrl+U)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('underline', false, null);
                                } catch (error) {
                                  console.error('Error executing underline:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatUnderlined fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Font Size">
                          <IconButton
                            size="small"
                            onClick={(e) => setFontSizeMenuAnchor(e.currentTarget)}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <TextFields fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Align Left">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('justifyLeft', false, null);
                                } catch (error) {
                                  console.error('Error executing justifyLeft:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatAlignLeft fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Align Center">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('justifyCenter', false, null);
                                } catch (error) {
                                  console.error('Error executing justifyCenter:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatAlignCenter fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Align Right">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('justifyRight', false, null);
                                } catch (error) {
                                  console.error('Error executing justifyRight:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatAlignRight fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        {/* Template Alignment Options */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.75rem',
                              color: '#6B7280',
                              fontWeight: 500,
                            }}
                          >
                            Template:
                          </Typography>
                          <Tooltip title="Left-align template layout">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTemplateAlignment('left');

                                // Update template.html_body immediately for live preview
                                if (template.html_body) {
                                  const updatedHtml = generateAlignedHtml(
                                    template.html_body,
                                    'left',
                                  );
                                  setTemplate((prev) => ({ ...prev, html_body: updatedHtml }));
                                }
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                backgroundColor:
                                  templateAlignment === 'left' ? '#E3F2FD' : '#FFFFFF',
                                color: templateAlignment === 'left' ? '#1976D2' : 'inherit',
                                '&:hover': {
                                  backgroundColor:
                                    templateAlignment === 'left' ? '#BBDEFB' : '#F5F5F5',
                                },
                              }}
                            >
                              <AlignHorizontalLeft fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Center-align template layout">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setTemplateAlignment('center');

                                // Update template.html_body immediately for live preview
                                if (template.html_body) {
                                  const updatedHtml = generateAlignedHtml(
                                    template.html_body,
                                    'center',
                                  );
                                  setTemplate((prev) => ({ ...prev, html_body: updatedHtml }));
                                }
                              }}
                              sx={{
                                border: '1px solid #E5E7EB',
                                backgroundColor:
                                  templateAlignment === 'center' ? '#E3F2FD' : '#FFFFFF',
                                color: templateAlignment === 'center' ? '#1976D2' : 'inherit',
                                '&:hover': {
                                  backgroundColor:
                                    templateAlignment === 'center' ? '#BBDEFB' : '#F5F5F5',
                                },
                              }}
                            >
                              <AlignHorizontalCenter fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Bullet List">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('insertUnorderedList', false, null);
                                } catch (error) {
                                  console.error('Error executing insertUnorderedList:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatListBulleted fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Numbered List">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('insertOrderedList', false, null);
                                } catch (error) {
                                  console.error('Error executing insertOrderedList:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatListNumbered fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Insert Link">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  const selection = iframeDoc.getSelection();
                                  const selectedText = selection.toString();
                                  setLinkText(selectedText || '');
                                  setLinkUrl('');
                                  setLinkDialogOpen(true);
                                } catch (error) {
                                  console.error('Error opening link dialog:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Insert Button">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setButtonUrl('');
                              setButtonText('Click Here');
                              setButtonColor('#28a745');
                              setButtonDialogOpen(true);
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <SmartButton fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Insert Placeholder">
                          <IconButton
                            size="small"
                            onClick={(e) => setPlaceholderMenuAnchor(e.currentTarget)}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <DataObject fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Text Color">
                          <IconButton
                            size="small"
                            onClick={(e) => setTextColorAnchor(e.currentTarget)}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FormatColorText fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Font Family">
                          <IconButton
                            size="small"
                            onClick={(e) => setFontFamilyMenuAnchor(e.currentTarget)}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <FontDownload fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Divider orientation="vertical" flexItem />

                        <Tooltip title="Undo (Ctrl+Z)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('undo', false, null);
                                } catch (error) {
                                  console.error('Error executing undo:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <UndoIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Redo (Ctrl+Y)">
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('redo', false, null);
                                } catch (error) {
                                  console.error('Error executing redo:', error);
                                }
                              }
                            }}
                            sx={{
                              border: '1px solid #E5E7EB',
                              backgroundColor: '#FFFFFF',
                            }}
                          >
                            <RedoIcon />
                          </IconButton>
                        </Tooltip>
                      </Paper>
                    </Box>

                    {template.html_body ? (
                      (() => {
                        // Check if it's a full HTML document
                        const trimmedHtml = template.html_body.trim().toLowerCase();
                        const isFullDocument =
                          trimmedHtml.startsWith('<!doctype') || trimmedHtml.startsWith('<html');

                        if (isFullDocument) {
                          // For full HTML documents, inject directly without parsing
                          // This preserves ALL CSS properties including border-radius, box-shadow, etc.
                          return (
                            <iframe
                              ref={previewIframeRef}
                              title="Email Preview"
                              srcDoc={template.html_body}
                              onLoad={() => {
                                // Don't enable design mode immediately - let user click to edit
                                if (previewIframeRef.current) {
                                  try {
                                    const iframeDoc =
                                      previewIframeRef.current.contentDocument ||
                                      previewIframeRef.current.contentWindow.document;

                                    // Enable design mode on first click to preserve CSS properties
                                    const enableEditingOnClick = () => {
                                      if (iframeDoc.designMode !== 'on') {
                                        iframeDoc.designMode = 'on';
                                        // Remove the event listener after first click
                                        iframeDoc.removeEventListener(
                                          'click',
                                          enableEditingOnClick,
                                        );
                                      }
                                    };

                                    iframeDoc.addEventListener('click', enableEditingOnClick);
                                  } catch (error) {
                                    console.error('Error setting up design mode:', error);
                                  }
                                }
                              }}
                              style={{
                                width: '100%',
                                height: '500px',
                                border: '2px solid #5A6ACF',
                                borderRadius: '4px',
                                backgroundColor: 'transparent',
                                transition: 'border 0.2s',
                              }}
                            />
                          );
                        }

                        // For partial HTML, wrap it with our container
                        let iframeBgColor = 'transparent';
                        let headContent = '';
                        let bodyContent = template.html_body;

                        return (
                          <iframe
                            ref={previewIframeRef}
                            title="Email Preview"
                            srcDoc={`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  ${headContent}
  <style>
    body {
      margin: 10px 0px 0px 0px;
      padding: 0;
      font-family: Arial, sans-serif;
      display: flex;
      justify-content: ${templateAlignment === 'center' ? 'center' : 'flex-start'};
    }
    .email-container {
      max-width: 640px;
      width: 100%;
    }
  </style>
</head>
<body style="background-color: ${iframeBgColor};">
  <div class="email-container">
    ${bodyContent}
  </div>
</body>
</html>`}
                            onLoad={() => {
                              // Don't enable design mode immediately - let user click to edit
                              // This preserves CSS properties like border-radius, box-shadow, etc.
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;

                                  // Apply template alignment to body
                                  if (iframeDoc.body) {
                                    iframeDoc.body.style.justifyContent =
                                      templateAlignment === 'center' ? 'center' : 'flex-start';
                                  }

                                  // Enable design mode on first click to preserve CSS properties
                                  const enableEditingOnClick = () => {
                                    if (iframeDoc.designMode !== 'on') {
                                      iframeDoc.designMode = 'on';
                                      // Remove the event listener after first click
                                      iframeDoc.removeEventListener('click', enableEditingOnClick);
                                    }
                                  };

                                  iframeDoc.addEventListener('click', enableEditingOnClick);
                                } catch (error) {
                                  console.error('Error setting up design mode:', error);
                                }
                              }
                            }}
                            style={{
                              width: '100%',
                              height: '500px',
                              border: '2px solid #5A6ACF',
                              borderRadius: '4px',
                              backgroundColor: 'transparent',
                              transition: 'border 0.2s',
                            }}
                          />
                        );
                      })()
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 400,
                          gap: 2,
                        }}
                      >
                        <Typography variant="h6" color="text.secondary">
                          No content yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add content in the HTML tab to see a preview
                        </Typography>
                      </Box>
                    )}
                  </>
                )}

                {/* Text Color Picker Popover */}
                <Popover
                  open={Boolean(textColorAnchor)}
                  anchorEl={textColorAnchor}
                  onClose={() => setTextColorAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Text Color
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(6, 1fr)',
                        gap: 1,
                      }}
                    >
                      {[
                        { name: 'Black', value: '#000000' },
                        { name: 'Dark Gray', value: '#4B5563' },
                        { name: 'Gray', value: '#9CA3AF' },
                        { name: 'Light Gray', value: '#D1D5DB' },
                        { name: 'Red', value: '#DC2626' },
                        { name: 'Orange', value: '#EA580C' },
                        { name: 'Amber', value: '#D97706' },
                        { name: 'Yellow', value: '#CA8A04' },
                        { name: 'Lime', value: '#65A30D' },
                        { name: 'Green', value: '#16A34A' },
                        { name: 'Emerald', value: '#059669' },
                        { name: 'Teal', value: '#0D9488' },
                        { name: 'Cyan', value: '#0891B2' },
                        { name: 'Sky', value: '#0284C7' },
                        { name: 'Blue', value: '#2563EB' },
                        { name: 'Indigo', value: '#4F46E5' },
                        { name: 'Violet', value: '#7C3AED' },
                        { name: 'Purple', value: '#9333EA' },
                        { name: 'Fuchsia', value: '#C026D3' },
                        { name: 'Pink', value: '#DB2777' },
                        { name: 'Rose', value: '#E11D48' },
                        { name: 'White', value: '#FFFFFF' },
                      ].map((color) => (
                        <Tooltip key={color.value} title={color.name}>
                          <Box
                            onClick={() => {
                              if (previewIframeRef.current) {
                                try {
                                  const iframeDoc =
                                    previewIframeRef.current.contentDocument ||
                                    previewIframeRef.current.contentWindow.document;
                                  iframeDoc.execCommand('foreColor', false, color.value);
                                  setTextColorAnchor(null);
                                } catch (error) {
                                  console.error('Error executing foreColor:', error);
                                }
                              }
                            }}
                            sx={{
                              width: 36,
                              height: 36,
                              backgroundColor: color.value,
                              border:
                                color.value === '#FFFFFF'
                                  ? '2px solid #E5E7EB'
                                  : '2px solid transparent',
                              borderRadius: 1,
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: '#4F46E5',
                                transform: 'scale(1.1)',
                              },
                              transition: 'all 0.2s',
                            }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Box>
                </Popover>

                {/* Font Family Menu */}
                <Menu
                  anchorEl={fontFamilyMenuAnchor}
                  open={Boolean(fontFamilyMenuAnchor)}
                  onClose={() => setFontFamilyMenuAnchor(null)}
                >
                  <MenuItem disabled sx={{ fontWeight: 600, color: colors.text.primary }}>
                    Font Family
                  </MenuItem>
                  {[
                    { name: 'Arial', value: 'Arial, sans-serif' },
                    {
                      name: 'Times New Roman',
                      value: "'Times New Roman', serif",
                    },
                    { name: 'Georgia', value: 'Georgia, serif' },
                    { name: 'Verdana', value: 'Verdana, sans-serif' },
                    { name: 'Courier New', value: "'Courier New', monospace" },
                    {
                      name: 'Trebuchet MS',
                      value: "'Trebuchet MS', sans-serif",
                    },
                    {
                      name: 'Comic Sans MS',
                      value: "'Comic Sans MS', cursive",
                    },
                    { name: 'Impact', value: 'Impact, sans-serif' },
                    {
                      name: 'Palatino',
                      value: "'Palatino Linotype', 'Book Antiqua', Palatino, serif",
                    },
                    { name: 'Garamond', value: 'Garamond, serif' },
                  ].map((font, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (previewIframeRef.current) {
                          try {
                            const iframeDoc =
                              previewIframeRef.current.contentDocument ||
                              previewIframeRef.current.contentWindow.document;

                            // Use execCommand for font family - much more reliable
                            iframeDoc.execCommand('fontName', false, font.value);

                            setFontFamilyMenuAnchor(null);
                          } catch (error) {
                            console.error('Error setting font family:', error);
                            showError('Failed to set font family');
                          }
                        }
                      }}
                      sx={{ fontFamily: font.value, fontSize: '0.875rem' }}
                    >
                      {font.name}
                    </MenuItem>
                  ))}
                </Menu>

                {/* Insert Link Dialog */}
                <Dialog
                  open={linkDialogOpen}
                  onClose={() => {
                    setLinkDialogOpen(false);
                    setLinkUrl('');
                    setLinkText('');
                  }}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      color: colors.text.primary,
                    }}
                  >
                    Insert Link
                  </DialogTitle>
                  <DialogContent>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Link Text"
                      value={linkText}
                      onChange={(e) => setLinkText(e.target.value)}
                      placeholder="Enter text to display"
                      size="small"
                      sx={{ mt: 2, mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="URL"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      placeholder="https://example.com"
                      size="small"
                      helperText="Must start with http:// or https://"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && linkUrl && linkText) {
                          e.preventDefault();
                          if (!linkUrl.startsWith('http://') && !linkUrl.startsWith('https://')) {
                            showError('URL must start with http:// or https://');
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
                              iframeDoc.execCommand('insertHTML', false, linkHtml);
                              setLinkDialogOpen(false);
                              setLinkUrl('');
                              setLinkText('');
                            } catch (error) {
                              console.error('Error inserting link:', error);
                              showError('Failed to insert link');
                            }
                          }
                        }
                      }}
                    />
                  </DialogContent>
                  <DialogActions sx={{ px: 1, py: 1 }}>
                    <Button
                      onClick={() => {
                        setLinkDialogOpen(false);
                        setLinkUrl('');
                        setLinkText('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ borderRadius: '4px' }}
                      onClick={() => {
                        if (linkUrl && linkText) {
                          if (!linkUrl.startsWith('http://') && !linkUrl.startsWith('https://')) {
                            showError('URL must start with http:// or https://');
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const linkHtml = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
                              iframeDoc.execCommand('insertHTML', false, linkHtml);
                              setLinkDialogOpen(false);
                              setLinkUrl('');
                              setLinkText('');
                            } catch (error) {
                              console.error('Error inserting link:', error);
                              showError('Failed to insert link');
                            }
                          }
                        } else {
                          showError('Please enter both link text and URL');
                        }
                      }}
                      variant="contained"
                      disabled={!linkUrl || !linkText}
                    >
                      Insert Link
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Insert Button Dialog */}
                <Dialog
                  open={buttonDialogOpen}
                  onClose={() => {
                    setButtonDialogOpen(false);
                    setButtonUrl('');
                    setButtonText('Click Here');
                    setButtonColor('#28a745');
                  }}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 500,
                      color: colors.text.primary,
                    }}
                  >
                    Insert Button
                  </DialogTitle>
                  <DialogContent sx={{ pt: 2 }}>
                    <TextField
                      autoFocus
                      fullWidth
                      label="Button Text"
                      value={buttonText}
                      onChange={(e) => setButtonText(e.target.value)}
                      placeholder="Click Here"
                      size="small"
                      sx={{ mt: 2, mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Button URL"
                      value={buttonUrl}
                      onChange={(e) => setButtonUrl(e.target.value)}
                      placeholder="https://example.com"
                      size="small"
                      helperText="Must start with http:// or https://"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0 },
                      }}
                      sx={{ mb: 2 }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && buttonUrl && buttonText) {
                          e.preventDefault();
                          if (
                            !buttonUrl.startsWith('http://') &&
                            !buttonUrl.startsWith('https://')
                          ) {
                            showError('URL must start with http:// or https://');
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const buttonHtml = `<div style="text-align: center; margin: 20px 0;"><a href="${buttonUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 12px 24px; background-color: ${buttonColor}; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: 500;">${buttonText}</a></div>`;
                              iframeDoc.execCommand('insertHTML', false, buttonHtml);
                              setButtonDialogOpen(false);
                              setButtonUrl('');
                              setButtonText('Click Here');
                              setButtonColor('#28a745');
                            } catch (error) {
                              console.error('Error inserting button:', error);
                              showError('Failed to insert button');
                            }
                          }
                        }
                      }}
                    />

                    {/* Button Color Picker */}
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: colors.text.primary,
                        }}
                      >
                        Button Color
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {[
                          { name: 'Green', value: '#28a745' },
                          { name: 'Blue', value: '#2563EB' },
                          { name: 'Red', value: '#DC2626' },
                          { name: 'Orange', value: '#EA580C' },
                          { name: 'Purple', value: '#9333EA' },
                          { name: 'Teal', value: '#0D9488' },
                          { name: 'Indigo', value: '#4F46E5' },
                          { name: 'Pink', value: '#DB2777' },
                          { name: 'Gray', value: '#4B5563' },
                          { name: 'Black', value: '#000000' },
                        ].map((color) => (
                          <Tooltip key={color.value} title={color.name}>
                            <Box
                              onClick={() => setButtonColor(color.value)}
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: color.value,
                                border:
                                  buttonColor === color.value
                                    ? '3px solid #4F46E5'
                                    : '2px solid #E5E7EB',
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                  borderColor: '#4F46E5',
                                  transform: 'scale(1.05)',
                                },
                                transition: 'all 0.2s',
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  </DialogContent>
                  <DialogActions sx={{ px: 1, py: 1 }}>
                    <Button
                      onClick={() => {
                        setButtonDialogOpen(false);
                        setButtonUrl('');
                        setButtonText('Click Here');
                        setButtonColor('#28a745');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ borderRadius: '4px' }}
                      onClick={() => {
                        if (buttonUrl && buttonText) {
                          if (
                            !buttonUrl.startsWith('http://') &&
                            !buttonUrl.startsWith('https://')
                          ) {
                            showError('URL must start with http:// or https://');
                            return;
                          }

                          if (previewIframeRef.current) {
                            try {
                              const iframeDoc =
                                previewIframeRef.current.contentDocument ||
                                previewIframeRef.current.contentWindow.document;
                              const buttonHtml = `<div style="text-align: center; margin: 20px 0;"><a href="${buttonUrl}" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 12px 24px; background-color: ${buttonColor}; color: #ffffff; text-decoration: none; border-radius: 4px; font-weight: 500;">${buttonText}</a></div>`;
                              iframeDoc.execCommand('insertHTML', false, buttonHtml);
                              setButtonDialogOpen(false);
                              setButtonUrl('');
                              setButtonText('Click Here');
                              setButtonColor('#28a745');
                            } catch (error) {
                              console.error('Error inserting button:', error);
                              showError('Failed to insert button');
                            }
                          }
                        } else {
                          showError('Please enter both button text and URL');
                        }
                      }}
                      variant="contained"
                      disabled={!buttonUrl || !buttonText}
                    >
                      Insert Button
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Font Size Menu */}
                <Menu
                  anchorEl={fontSizeMenuAnchor}
                  open={Boolean(fontSizeMenuAnchor)}
                  onClose={() => setFontSizeMenuAnchor(null)}
                >
                  <MenuItem disabled sx={{ fontWeight: 600, color: colors.text.primary }}>
                    Font Size
                  </MenuItem>
                  {[
                    { label: '12px', value: '12px' },
                    { label: '14px', value: '14px' },
                    { label: '16px', value: '16px' },
                    { label: '18px', value: '18px' },
                    { label: '20px', value: '20px' },
                    { label: '24px', value: '24px' },
                    { label: '28px', value: '28px' },
                    { label: '32px', value: '32px' },
                  ].map((size, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (previewIframeRef.current) {
                          try {
                            const iframeDoc =
                              previewIframeRef.current.contentDocument ||
                              previewIframeRef.current.contentWindow.document;

                            // Get the current selection
                            const selection = iframeDoc.getSelection();
                            if (selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              const selectedText = range.toString();

                              if (selectedText) {
                                // Extract the contents and wrap in span
                                const fragment = range.extractContents();
                                const span = iframeDoc.createElement('span');
                                span.style.fontSize = size.value;
                                span.appendChild(fragment);
                                range.insertNode(span);

                                // Restore selection
                                selection.removeAllRanges();
                                const newRange = iframeDoc.createRange();
                                newRange.selectNodeContents(span);
                                selection.addRange(newRange);
                              }
                            }

                            setFontSizeMenuAnchor(null);
                          } catch (error) {
                            console.error('Error setting font size:', error);
                            showError('Failed to set font size. Try selecting less text.');
                          }
                        }
                      }}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {size.label}
                    </MenuItem>
                  ))}
                </Menu>

                {/* Placeholder Menu */}
                <Menu
                  anchorEl={placeholderMenuAnchor}
                  open={Boolean(placeholderMenuAnchor)}
                  onClose={() => setPlaceholderMenuAnchor(null)}
                >
                  <MenuItem disabled sx={{ fontWeight: 600, color: colors.text.primary }}>
                    Placeholders
                  </MenuItem>
                  {availablePlaceholders.map((placeholder, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (previewIframeRef.current) {
                          try {
                            const iframeDoc =
                              previewIframeRef.current.contentDocument ||
                              previewIframeRef.current.contentWindow.document;
                            iframeDoc.execCommand('insertHTML', false, placeholder.token);
                            setPlaceholderMenuAnchor(null);
                          } catch (error) {
                            console.error('Error inserting placeholder:', error);
                            showError('Failed to insert placeholder');
                          }
                        }
                      }}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      {placeholder.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            {/* HTML Tab - Now second (index 1) */}
            {activeTab === 1 && (
              <TextField
                multiline
                fullWidth
                value={template.html_body}
                onChange={(e) => handleFieldChange('html_body', e.target.value)}
                variant="outlined"
                minRows={20}
                sx={{
                  borderRadius: '4px',
                  maxHeight: '500px',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  '& .MuiInputBase-root': {
                    fontFamily: 'monospace',
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Test Send Dialog */}
      <Dialog open={testSendDialogOpen} onClose={handleTestSendClose} maxWidth="sm" fullWidth>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test email with dummy placeholder data to verify the template rendering.
          </Typography>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
            The email will be sent with sample data including: John Doe, Sample Organization, ₹1000
            donation, etc.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Recipient Email"
            type="email"
            value={testSendEmail}
            onChange={(e) => setTestSendEmail(e.target.value)}
            placeholder="test@example.com"
            helperText="Enter the email address to receive the test"
            FormHelperTextProps={{
              sx: { marginLeft: 0 },
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleTestSend();
              }
            }}
            inputProps={{
              'aria-label': 'test recipient email',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestSendClose} disabled={testSending}>
            Cancel
          </Button>
          <Button
            sx={{ borderRadius: '4px' }}
            onClick={handleTestSend}
            variant="contained"
            disabled={testSending}
            startIcon={testSending ? <CircularProgress size={20} /> : <Send />}
          >
            {testSending ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDelete}
        title="Delete Template"
        itemName="template"
        deleting={deleting}
      >
        <Typography variant="body2" sx={{ color: colors.text.secondary, px: 2 }}>
          Are you sure you want to delete the template "{template.name}"?
        </Typography>
        <Typography variant="body2" sx={{ color: colors.text.secondary, px: 2, mt: 1 }}>
          This action will soft-delete the template. It can be recovered by an administrator.
        </Typography>
      </DeleteConfirmationDialog>

      {/* Please Note Modal - Important Information */}
      <Dialog
        open={unsubscribeModalOpen}
        onClose={() => setUnsubscribeModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            backgroundColor: '#F0F9FF',
            borderBottom: '1px solid #BAE6FD',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info sx={{ fontSize: 22, color: '#0284C7' }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#0C4A6E',
                fontSize: '16px',
              }}
            >
              Important Information
            </Typography>
          </Box>
          <IconButton
            onClick={() => setUnsubscribeModalOpen(false)}
            sx={{
              color: '#0C4A6E',
              '&:hover': {
                backgroundColor: 'transparent',
                color: '#08334D',
              },
            }}
            aria-label="close"
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pb: 3 }}>
          {/* Section 1: How to Add Unsubscribe */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#0F172A',
                fontSize: '16px',
                fontWeight: 600,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              1. How to Add Unsubscribe in Template
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#475569',
                fontSize: '14px',
                lineHeight: 1.6,
                mb: 2,
              }}
            >
              Use these simple tags in your email template:
            </Typography>

            {/* HTML Version */}
            <Box sx={{ mb: 2, ml: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: colors.text.primary,
                  fontSize: '13px',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                HTML Version:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  p: 1.5,
                  borderRadius: '4px',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Box
                  component="code"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#0284C7',
                    wordBreak: 'break-all',
                  }}
                >
                  {'<a href="[unsubscribe]">Click here to unsubscribe</a>'}
                </Box>
              </Box>
            </Box>

            {/* Plain Text Version */}
            {/* <Box sx={{ mb: 2, ml: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  color: colors.text.primary,
                  fontSize: '13px',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Plain Text Version:
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  p: 1.5,
                  borderRadius: '4px',
                  border: '1px solid #E2E8F0',
                }}
              >
                <Box
                  component="code"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#0284C7',
                  }}
                >
                  [unsubscribe]
                </Box>
              </Box>
            </Box> */}

            <Alert severity="info" sx={{ ml: 2, fontSize: '13px' }}>
              Unsubscribe will not work in test emails. The link will be automatically generated
              when emails are sent to contacts.
            </Alert>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section 2: Donation Placeholders */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#0F172A',
                fontSize: '16px',
                fontWeight: 600,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              2. Donation Placeholders
            </Typography>
            <Alert severity="warning" sx={{ mb: 2, fontSize: '13px' }}>
              Donation placeholders work only for receipt emails sent from the transaction module.
            </Alert>
            <Typography
              variant="body2"
              sx={{
                color: '#475569',
                fontSize: '14px',
                lineHeight: 1.6,
                ml: 2,
              }}
            >
              Available donation placeholders:
            </Typography>
            <Box sx={{ ml: 4, mt: 1 }}>
              <Typography
                component="ul"
                sx={{
                  color: '#475569',
                  fontSize: '13px',
                  lineHeight: 1.8,
                  pl: 2,
                }}
              >
                <li>
                  <code
                    style={{ backgroundColor: '#F1F5F9', padding: '2px 6px', borderRadius: '3px' }}
                  >
                    {'{{transaction.amount}}'}
                  </code>{' '}
                  - Donation amount
                </li>
                <li>
                  <code
                    style={{ backgroundColor: '#F1F5F9', padding: '2px 6px', borderRadius: '3px' }}
                  >
                    {'{{transaction.trust_name}}'}
                  </code>{' '}
                  - Trust name
                </li>
                <li>
                  <code
                    style={{ backgroundColor: '#F1F5F9', padding: '2px 6px', borderRadius: '3px' }}
                  >
                    {'{{transaction.donation_date}}'}
                  </code>{' '}
                  - Date of donation
                </li>
                <li>
                  <code
                    style={{ backgroundColor: '#F1F5F9', padding: '2px 6px', borderRadius: '3px' }}
                  >
                    {'{{transaction.payment_method}}'}
                  </code>{' '}
                  - Payment method
                </li>
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Section 3: Save and Check Before Sending */}
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: '#0F172A',
                fontSize: '16px',
                fontWeight: 600,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              3. Save and Check Before Sending
            </Typography>
            <Alert severity="error" sx={{ mb: 2, fontSize: '13px' }}>
              Always save your template and verify changes before sending emails to contacts!
            </Alert>
            <Typography
              variant="body2"
              sx={{
                color: '#475569',
                fontSize: '14px',
                lineHeight: 1.6,
                ml: 2,
              }}
            >
              Best practices:
            </Typography>
            <Box sx={{ ml: 4, mt: 1 }}>
              <Typography
                component="ul"
                sx={{
                  color: '#475569',
                  fontSize: '13px',
                  lineHeight: 1.8,
                  pl: 2,
                }}
              >
                <li>Use the "Test Send" button to send a test email </li>
                <li>Verify all placeholders are replaced correctly</li>
                <li>Check formatting and layout of email in preview</li>
                <li>Review unsubscribe link placement</li>
                <li>
                  If the contact type is Organization, {'{{contact.first_name}}'} is automatically
                  replaced with the organization name.
                </li>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EmailTemplateEditor;
